import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import FunctionalButton from "components/layout/FunctionalButton";
import WifiConnectDialog from "components/control/settings/WifiConnectDialog";
import NetworkIcon from "components/layout/status/NetworkIcon";
import { useARC } from "providers/ARC/ARCProvider";
import { useToast } from "providers/ToastProvider";
import {
    NETWORK_ROW_CONNECT_BUTTON_CLASS,
    NETWORK_ROW_CONNECT_TITLE_CLASS,
    NETWORK_ROW_DISCONNECT_BUTTON_CLASS,
    NETWORK_ROW_DISCONNECT_TITLE_CLASS,
} from "lib/consts/networkSettingsActionButtons";
import {
    dedupeWifiNetworksBySsid,
    fetchWifiScan,
    fetchWifiStatus,
    getWifiApiBaseUrl,
    postWifiDisconnect,
    type WifiNetwork,
} from "lib/wifiApi";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { formatPingMs, useZeroHostPing } from "lib/hooks/useZeroHostPing";
import {
    isWifiModuleNotReadyMessage,
    toUserFacingApiError,
} from "lib/utils/userFacingApiError";

const SCAN_INTERVAL_MS = 5000;

/** Same proportions as former colgroup (38% / 18% / 24% / 20%). `items-center` per row aligns Action buttons when SSID wraps. */
const WIFI_TABLE_GRID =
    "grid w-full grid-cols-[minmax(0,38fr)_minmax(0,18fr)_minmax(0,24fr)_minmax(0,20fr)] items-center gap-x-0";

function sortNetworks(a: WifiNetwork, b: WifiNetwork): number {
    const sa = a.signal ?? -1;
    const sb = b.signal ?? -1;
    return sb - sa;
}

function sortWithConnectedFirst(list: WifiNetwork[], connectedSsid: string | null): WifiNetwork[] {
    return [...list].sort((a, b) => {
        if (connectedSsid) {
            const aOn = a.ssid === connectedSsid;
            const bOn = b.ssid === connectedSsid;
            if (aOn !== bOn) return aOn ? -1 : 1;
        }
        return sortNetworks(a, b);
    });
}

const WifiSettingsPanel: React.FC = () => {
    const { t } = useTranslation(["settings", "common"]);
    const { addToast } = useToast();
    const { selectedARC } = useARC();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const ping = useZeroHostPing();
    const baseUrl = useMemo(
        () => getWifiApiBaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const [networks, setNetworks] = useState<WifiNetwork[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** Radio / wifi-scan service still coming up (HTTP 502 / nmcli not ready). */
    const [moduleLoading, setModuleLoading] = useState(false);
    const scanInFlight = useRef(false);
    const [connectTarget, setConnectTarget] = useState<WifiNetwork | null>(null);
    const [connectedSsid, setConnectedSsid] = useState<string | null>(null);
    const [disconnectBusy, setDisconnectBusy] = useState(false);
    /** SSID kept highlighted as "Disconnecting…" until disconnect + refresh finish. */
    const [disconnectingSsid, setDisconnectingSsid] = useState<string | null>(null);

    const runScan = useCallback(async (opts?: { force?: boolean }) => {
        if (!baseUrl) return;
        if (scanInFlight.current) {
            if (!opts?.force) return;
            // Wait out the in-flight poll so disconnect refresh does not overlap nmcli.
            const start = Date.now();
            while (scanInFlight.current && Date.now() - start < 30_000) {
                await new Promise((r) => window.setTimeout(r, 50));
            }
        }
        scanInFlight.current = true;
        setLoading(true);
        setError(null);
        try {
            const data = await fetchWifiScan(baseUrl);

            let cur: string | null = null;
            try {
                const st = await fetchWifiStatus(baseUrl);
                cur =
                    st.error == null || st.error === ""
                        ? st.ssid ?? st.connection ?? null
                        : null;
            } catch {
                cur = null;
            }
            setConnectedSsid(cur);
            setNetworks(sortWithConnectedFirst(dedupeWifiNetworksBySsid(data.networks), cur));
            setModuleLoading(false);
        } catch (e) {
            setNetworks([]);
            setConnectedSsid(null);
            const raw = e instanceof Error ? e.message : "";
            if (isWifiModuleNotReadyMessage(raw)) {
                setModuleLoading(true);
                setError(null);
            } else {
                setModuleLoading(false);
                setError(toUserFacingApiError(e, t("wifi.scanFailed")));
            }
        } finally {
            scanInFlight.current = false;
            setLoading(false);
        }
    }, [baseUrl, t]);

    useEffect(() => {
        if (!baseUrl) {
            setError(t("wifi.hostNotSet"));
            setModuleLoading(false);
            setNetworks([]);
            setConnectedSsid(null);
            setDisconnectingSsid(null);
            return;
        }
        setError(null);
        void runScan();
        const id = window.setInterval(() => void runScan(), SCAN_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [baseUrl, runScan, t]);

    const handleDisconnect = useCallback(async () => {
        if (!baseUrl || disconnectBusy) return;
        const ssid = connectedSsid;
        setDisconnectBusy(true);
        setDisconnectingSsid(ssid);
        try {
            const res = await postWifiDisconnect(baseUrl);
            addToast({ type: "success", message: res.message || t("wifi.disconnected") });
            await runScan({ force: true });
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(e, t("wifi.disconnectFailed")),
            });
        } finally {
            setDisconnectBusy(false);
            setDisconnectingSsid(null);
        }
    }, [addToast, baseUrl, connectedSsid, disconnectBusy, runScan, t]);

    return (
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
            <div className="shrink-0 border-b border-black80 pb-4">
                <p className="text-h4 font-semibold text-white">{t("wifi.title")}</p>
                {baseUrl && !error && !moduleLoading ? (
                    disconnectBusy && disconnectingSsid ? (
                        <p className="mt-1 text-h5 text-primary2" role="status">
                            {t("wifi.disconnectingFrom", { ssid: disconnectingSsid })}
                        </p>
                    ) : connectedSsid ? (
                        <p className="mt-1 text-h5 text-primary2">
                            {t("wifi.connected", { ssid: connectedSsid })}
                        </p>
                    ) : loading && networks.length === 0 ? null : (
                        <p className="mt-1 text-h5 text-black60">
                            {t("wifi.notConnected")}
                        </p>
                    )
                ) : null}
                <p
                    className="mt-2 text-h5 text-black40"
                    title={t("wifi.pingTitle")}
                >
                    {t("wifi.ping")}{" "}
                    <span className="tabular-nums text-white">{formatPingMs(ping)}</span>
                </p>
            </div>

            {error ? (
                <p className="mt-4 shrink-0 text-h5 text-red-400" role="alert">
                    {error}
                </p>
            ) : null}

            <div className="mt-4 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-r10 border border-black80 bg-black100 min-h-[min(50vh,22rem)]">
                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto">
                    {moduleLoading ? (
                        <div className="flex min-h-0 flex-1 flex-col items-center justify-center p-8">
                            <p className="text-h5 text-black40" role="status">
                                {t("wifi.moduleLoading")}
                            </p>
                        </div>
                    ) : loading && networks.length === 0 ? (
                        <div className="flex min-h-0 flex-1 flex-col items-center justify-center p-8">
                            <p className="text-h5 text-black40">{t("wifi.scanning")}</p>
                        </div>
                    ) : networks.length === 0 ? (
                        <div className="flex min-h-0 flex-1 flex-col items-center justify-center p-8">
                            <p className="text-h5 text-black60">{t("wifi.noNetworks")}</p>
                        </div>
                    ) : (
                        <div className="w-full text-h5" role="table" aria-label={t("wifi.tableAriaLabel")}>
                            <div
                                className={`${WIFI_TABLE_GRID} border-b border-black80 px-3 py-2.5 text-black40`}
                                role="row"
                            >
                                <div role="columnheader" className="min-w-0 font-semibold text-left">
                                    {t("wifi.network")}
                                </div>
                                <div role="columnheader" className="font-semibold text-center">
                                    {t("wifi.signal")}
                                </div>
                                <div role="columnheader" className="min-w-0 font-semibold text-left">
                                    {t("wifi.security")}
                                </div>
                                <div role="columnheader" className="font-semibold text-center">
                                    {t("wifi.action")}
                                </div>
                            </div>
                            {networks.map((n, idx) => {
                                const isDisconnecting =
                                    disconnectBusy &&
                                    disconnectingSsid != null &&
                                    n.ssid === disconnectingSsid;
                                const isConnected =
                                    !isDisconnecting &&
                                    connectedSsid != null &&
                                    n.ssid === connectedSsid;
                                const showAsActive = isConnected || isDisconnecting;
                                return (
                                    <div
                                        key={`${idx}-${n.ssid}-${n.signal}`}
                                        role="row"
                                        className={`${WIFI_TABLE_GRID} border-b border-black80 px-3 py-2.5 last:border-b-0 ${
                                            showAsActive
                                                ? "border-l-2 border-l-primary2 bg-black80/50"
                                                : ""
                                        }`}
                                    >
                                        <div role="cell" className="min-w-0 break-words pr-2 text-white">
                                            <span>{n.ssid}</span>
                                            {isDisconnecting ? (
                                                <span className="ml-2 inline-block align-middle rounded-r3 bg-primary2/25 px-2 py-0.5 text-h5 text-primary2">
                                                    {t("wifi.disconnectingBadge")}
                                                </span>
                                            ) : isConnected ? (
                                                <span className="ml-2 inline-block align-middle rounded-r3 bg-primary2/25 px-2 py-0.5 text-h5 text-primary2">
                                                    {t("wifi.connectedBadge")}
                                                </span>
                                            ) : null}
                                        </div>
                                        <div role="cell" className="flex justify-center px-1">
                                            {n.signal == null ? (
                                                <span className="text-black40">—</span>
                                            ) : (
                                                <div
                                                    className="flex justify-center"
                                                    title={`${n.signal}%`}
                                                >
                                                    <NetworkIcon
                                                        type=""
                                                        status={n.signal}
                                                        className="shrink-0"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div role="cell" className="min-w-0 break-words px-1 text-black40">
                                            {n.security ?? "—"}
                                        </div>
                                        <div
                                            role="cell"
                                            className="flex justify-center pl-1"
                                        >
                                            {showAsActive ? (
                                                <FunctionalButton
                                                    className={NETWORK_ROW_DISCONNECT_BUTTON_CLASS}
                                                    title={
                                                        isDisconnecting
                                                            ? t("wifi.disconnecting")
                                                            : t("common:disconnect")
                                                    }
                                                    titleClassName={NETWORK_ROW_DISCONNECT_TITLE_CLASS}
                                                    disabled={!baseUrl || disconnectBusy}
                                                    onClick={() => void handleDisconnect()}
                                                />
                                            ) : (
                                                <FunctionalButton
                                                    className={NETWORK_ROW_CONNECT_BUTTON_CLASS}
                                                    title={t("common:connect")}
                                                    titleClassName={NETWORK_ROW_CONNECT_TITLE_CLASS}
                                                    disabled={!baseUrl || disconnectBusy}
                                                    onClick={() => setConnectTarget(n)}
                                                />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <WifiConnectDialog
                visible={connectTarget !== null}
                network={connectTarget}
                baseUrl={baseUrl}
                onClose={() => setConnectTarget(null)}
                onConnected={() => void runScan()}
            />
        </div>
    );
};

export default WifiSettingsPanel;
